import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  ClockCounterClockwise, Download, MagnifyingGlass, Shield, ShieldCheck,
  FileText, User, Funnel
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '../lib/logger';

const API = (typeof window !== 'undefined' && window.location?.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + '/api';
const api = axios.create({ baseURL: API, withCredentials: true });

const ACTION_COLORS = {
  create: 'bg-emerald-500', update: 'bg-blue-500', delete: 'bg-red-500',
  login: 'bg-purple-500', logout: 'bg-zinc-500', role_change: 'bg-amber-500',
  export: 'bg-cyan-500', import: 'bg-indigo-500', stage_change: 'bg-orange-500',
  document_upload: 'bg-teal-500', workflow_execute: 'bg-violet-500',
};

const ENTITY_TYPES = ['contact', 'company', 'deal', 'task', 'document', 'procurement', 'workflow', 'user', 'data_room', 'business_unit', 'listing'];
const ACTION_TYPES = ['create', 'update', 'delete', 'login', 'logout', 'role_change', 'export', 'import', 'stage_change', 'document_upload'];
const COLORS = ['#0047FF', '#10B981', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899', '#14B8A6', '#6366F1'];

export default function AuditTrailPage() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      const params = { limit, offset: page * limit };
      if (entityFilter) params.entity_type = entityFilter;
      if (actionFilter) params.action = actionFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (complianceFilter) params.compliance_tag = complianceFilter;
      const response = await api.get('/audit-logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied - insufficient permissions');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally { setLoading(false); }
  }, [page, entityFilter, actionFilter, startDate, endDate, complianceFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/audit-logs/stats');
      setStats(response.data);
    } catch (error) { logger.error(error); }
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const handleExport = async () => {
    try {
      const params = { format: 'csv' };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (complianceFilter) params.compliance_tag = complianceFilter;
      const response = await api.get('/audit-logs/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url; link.download = 'audit_log_export.csv'; link.click();
      URL.revokeObjectURL(url);
      toast.success('Audit log exported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    }
  };

  const actionPieData = stats?.action_stats ? Object.entries(stats.action_stats).map(([k, v]) => ({ name: k, value: v })) : [];
  const entityPieData = stats?.entity_stats ? Object.entries(stats.entity_stats).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('audit.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('audit.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={handleExport} className="rounded-sm" data-testid="export-audit-btn">
          <Download size={18} className="mr-2" /> {t('audit.exportLogs')}
        </Button>
      </div>

      {/* Compliance Badges */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <ClockCounterClockwise size={18} className="text-primary" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.totalLogs')}</p>
            </div>
            <p className="text-2xl font-bold">{stats.total_logs}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={18} className="text-blue-500" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">GDPR Tagged</p>
            </div>
            <p className="text-2xl font-bold">{stats.compliance?.gdpr || 0}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} className="text-emerald-500" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">SOX Tagged</p>
            </div>
            <p className="text-2xl font-bold">{stats.compliance?.sox || 0}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <User size={18} className="text-amber-500" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Active Users</p>
            </div>
            <p className="text-2xl font-bold">{stats.top_users?.length || 0}</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs"><FileText size={16} className="mr-1" /> Logs</TabsTrigger>
          <TabsTrigger value="analytics"><Funnel size={16} className="mr-1" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <Select value={entityFilter || "all_entities"} onValueChange={(v) => { setEntityFilter(v === "all_entities" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Entities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_entities">All Entities</SelectItem>
                {ENTITY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={actionFilter || "all_actions"} onValueChange={(v) => { setActionFilter(v === "all_actions" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_actions">All Actions</SelectItem>
                {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={complianceFilter || "all_compliance"} onValueChange={(v) => { setComplianceFilter(v === "all_compliance" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Compliance" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_compliance">All</SelectItem>
                <SelectItem value="gdpr">GDPR</SelectItem>
                <SelectItem value="sox">SOX</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }} className="w-40" placeholder="Start" />
            <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }} className="w-40" placeholder="End" />
          </div>

          {/* Logs Table */}
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.timestamp')}</th>
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.user')}</th>
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.action')}</th>
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.entity')}</th>
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Details</th>
                  <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{t('audit.compliance')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No audit logs found</td></tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`audit-log-${log.id}`}>
                      <td className="p-3 text-xs font-mono text-muted-foreground whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm font-medium">{log.user_name}</p>
                          <p className="text-xs text-muted-foreground">{log.user_role}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={`${ACTION_COLORS[log.action] || 'bg-zinc-500'} text-white text-xs`}>{log.action}</Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-sm capitalize">{log.entity_type}</span>
                        {log.entity_name && <p className="text-xs text-muted-foreground">{log.entity_name}</p>}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-48 truncate">
                        {log.changes ? JSON.stringify(log.changes).slice(0, 80) : log.ip_address || '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {log.compliance_tags?.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs uppercase">{tag}</Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Actions Distribution */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Actions Distribution</h3>
              <div className="h-72">
                {actionPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={actionPieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {actionPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No data</p>}
              </div>
            </div>

            {/* Entity Distribution */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Entity Distribution</h3>
              <div className="h-72">
                {entityPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={entityPieData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No data</p>}
              </div>
            </div>
          </div>

          {/* Top Users */}
          {stats?.top_users?.length > 0 && (
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Most Active Users</h3>
              <div className="space-y-2">
                {stats.top_users.map((u, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-muted-foreground w-6">#{i + 1}</span>
                      <span className="font-medium text-sm">{u.user_name}</span>
                    </div>
                    <Badge variant="secondary">{u.count} actions</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
